import React from "react";
import PropTypes from "prop-types";
import { Label, Input } from "reactstrap";

/**
 * FormPhotoUpload
 * Componente de upload de foto circular com botão de câmera flutuante.
 * Agora inclui compressão de imagem para evitar erros de limite do Firestore (1MB).
 */
const FormPhotoUpload = ({
    photoPreview,
    onChange,
    size = "md",
    className = "",
    objectFit = "cover",
    roundedCircle = true
}) => {
    // Tamanhos baseados no NEO Avatar
    const dimensions = {
        xs: "40px",
        sm: "60px",
        md: "90px",
        lg: "130px",
        xl: "170px"
    };

    const containerSize = dimensions[size] || dimensions.md;
    const buttonSize = size === "xs" || size === "sm" ? "22px" : "36px";
    const iconSize = size === "xs" || size === "sm" ? "12px" : "18px";

    /**
     * Comprime a imagem usando Canvas antes de enviar o Base64, preservando transparência
     */
    const compressImage = (base64Str, mimeType) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.src = base64Str;
            img.onload = () => {
                const canvas = document.createElement("canvas");
                const MAX_WIDTH = 800;
                const MAX_HEIGHT = 800;
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > MAX_WIDTH) {
                        height *= MAX_WIDTH / width;
                        width = MAX_WIDTH;
                    }
                } else {
                    if (height > MAX_HEIGHT) {
                        width *= MAX_HEIGHT / height;
                        height = MAX_HEIGHT;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");

                if (mimeType !== "image/png" && mimeType !== "image/webp") {
                    // Preenche com branco para evitar fundo preto transparente no JPEG
                    ctx.fillStyle = "#FFFFFF";
                    ctx.fillRect(0, 0, width, height);
                }

                ctx.drawImage(img, 0, 0, width, height);

                // Usa o tipo original se for png ou webp (para manter transparência), caso contrário usa jpeg com qualidade
                const outType = (mimeType === "image/png" || mimeType === "image/webp") ? mimeType : "image/jpeg";
                resolve(canvas.toDataURL(outType, 0.7));
            };
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Verifica se é maior que 2MB para avisar ou processar (opcional)
            const reader = new FileReader();
            reader.onloadend = async () => {
                const compressed = await compressImage(reader.result, file.type);
                onChange(compressed);
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <Label className={`d-inline-block position-relative m-0 cursor-pointer ${className}`} style={{ padding: "5px", cursor: "pointer" }}>
            <div
                className="position-relative"
                style={{
                    width: containerSize,
                    height: containerSize,
                }}
            >
                <div 
                    className={`w-100 h-100 border border-3 border-white shadow ${!photoPreview ? 'bg-light' : 'bg-transparent'} d-flex align-items-center justify-content-center overflow-hidden`}
                    style={{ borderRadius: roundedCircle ? "50%" : "12px" }}
                >
                    {photoPreview ? (
                        <img
                            src={photoPreview}
                            alt="Preview"
                            className="w-100 h-100"
                            style={{ objectFit: objectFit, borderRadius: roundedCircle ? "50%" : "12px" }}
                        />
                    ) : (
                        <div className="text-center text-muted">
                            <i className="mdi mdi-image-plus font-size-24 opacity-50"></i>
                        </div>
                    )}
                </div>

                <div
                    className="position-absolute d-flex align-items-center justify-content-center bg-primary text-white rounded-circle shadow-lg m-0"
                    style={{
                        width: buttonSize,
                        height: buttonSize,
                        zIndex: 10,
                        border: "2px solid white",
                        bottom: "0px",
                        right: "0px",
                        transform: roundedCircle ? "translate(5%, 5%)" : "translate(15%, 15%)",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.2)"
                    }}
                >
                    <i className={`mdi mdi-camera`} style={{ fontSize: iconSize }}></i>
                </div>
            </div>

            <Input
                type="file"
                accept="image/*"
                className="d-none"
                onChange={handleFileChange}
            />

            {(size === "lg" || size === "xl") && (
                <div className="text-center mt-2">
                    <span className="text-muted small fw-medium">Alterar foto</span>
                </div>
            )}
        </Label>
    );
};

FormPhotoUpload.propTypes = {
    photoPreview: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    size: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl"]),
    className: PropTypes.string,
    objectFit: PropTypes.string,
    roundedCircle: PropTypes.bool,
};

export default FormPhotoUpload;
